import { tryOrCatchMessageError } from '@/utils/tryOrCatchMessageError';
import { useCallback, useState } from 'react';

export interface UseRequestUpdateOptions<Entity, Response, Id = string | number> {
  request: (id: Id, entity: Partial<Entity>, companyId?: string) => Promise<Response> | Response;
  externalLoading?: boolean;
  callback?: (response: Response, entity: Partial<Entity>, companyId?: string) => void;
}

export const useRequestUpdate = <Entity, Response = void, Id = string | number>({
  request,
  externalLoading,
  callback,
}: UseRequestUpdateOptions<Entity, Response, Id>) => {
  const [loading, setLoading] = useState(false);

  const requestUpdate = useCallback(
    async (id: Id, entity: Partial<Entity>, companyIdToUpdate?: string) => {
      if (externalLoading || loading) {
        return;
      }

      await tryOrCatchMessageError(
        async () => {
          const response = await request(id, entity, companyIdToUpdate);
          callback?.(response, entity, companyIdToUpdate);
        },
        {
          tryBefore: () => setLoading(true),
          tryAfter: () => setLoading(false),
          messageErrorDefault: 'A solicitação de alteração falhou, tente novamente mais tarde.',
        },
      );
    },
    [externalLoading, callback, request, loading],
  );

  return {
    requestUpdate,
    loading,
  };
};
